// src/hooks/useUserOrders.jsx
import { useState, useEffect, useCallback, useMemo } from "react";
import supabase from "../SupaCredentials";
import { useSupaContext } from "../contexts/SupaContext";
import toast from "react-hot-toast";

export const useUserOrders = () => {
  const { user } = useSupaContext();

  // Estados principales
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Función para obtener órdenes del usuario
  const fetchUserOrders = useCallback(async () => {
    if (!user?.id) {
      setOrders([]);
      setIsLoading(false);
      return;
    }

    try {
      setError(null);

      // Obtener órdenes con sus detalles completos
      const { data: ordersData, error: ordersError } = await supabase
        .from("orders")
        .select(
          `
          *,
          order_details (
            *,
            products (
              name,
              sku_code,
              is_jug,
              is_refill
            ),
            delivered_jug:delivered_jug_id (
              id,
              barcode
            ),
            returned_jug:returned_jug_id (
              id, 
              barcode
            ),
            addresses!delivery_address_id (
              address,
              city,
              details
            )
          )
        `
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (ordersError) throw ordersError;

      // Obtener información de pagos para cada orden
      const ordersWithPayments = await Promise.all(
        (ordersData || []).map(async (order) => {
          const { data: paymentsData } = await supabase
            .from("payments")
            .select("amount, payment_status, payment_date")
            .eq("order_id", order.id);

          const totalPaid =
            paymentsData?.reduce(
              (sum, payment) =>
                payment.payment_status === "paid"
                  ? sum + Number(payment.amount)
                  : sum,
              0
            ) || 0;

          const balanceDue = Number(order.total) - totalPaid;

          return {
            ...order,
            payments: paymentsData || [],
            total_paid: totalPaid,
            balance_due: balanceDue,
            payment_count: paymentsData?.length || 0,
            last_payment_date:
              paymentsData?.length > 0
                ? paymentsData.sort(
                    (a, b) =>
                      new Date(b.payment_date) - new Date(a.payment_date)
                  )[0].payment_date
                : null,
          };
        })
      );

      // Procesar las órdenes para incluir información adicional
      const processedOrders =
        ordersWithPayments?.map((order) => ({
          ...order,
          // Información básica de la orden
          order_date_formatted: new Date(order.created_at).toLocaleDateString(
            "es-CO",
            {
              year: "numeric",
              month: "short",
              day: "numeric",
            }
          ),
          order_time_formatted: new Date(order.created_at).toLocaleTimeString(
            "es-CO",
            {
              hour: "2-digit",
              minute: "2-digit",
            }
          ),

          // Estados de pago
          needs_payment:
            (order.payment_status === "pending" ||
              order.payment_status === "failed") &&
            !order.is_incomplete &&
            order.order_status !== "canceled",

          // Formatear montos (incluyendo shipping_cost correctamente)
          subtotal_formatted: new Intl.NumberFormat("es-CO", {
            style: "currency",
            currency: "COP",
            minimumFractionDigits: 0,
          }).format(order.subtotal || 0),
          shipping_cost_formatted: new Intl.NumberFormat("es-CO", {
            style: "currency",
            currency: "COP",
            minimumFractionDigits: 0,
          }).format(order.shipping_cost || 0),
          order_total_formatted: new Intl.NumberFormat("es-CO", {
            style: "currency",
            currency: "COP",
            minimumFractionDigits: 0,
          }).format(order.total || 0),
          total_paid_formatted: new Intl.NumberFormat("es-CO", {
            style: "currency",
            currency: "COP",
            minimumFractionDigits: 0,
          }).format(order.total_paid || 0),
          balance_due_formatted: new Intl.NumberFormat("es-CO", {
            style: "currency",
            currency: "COP",
            minimumFractionDigits: 0,
          }).format(order.balance_due || 0),

          // Procesar detalles de la orden
          order_details:
            order.order_details?.map((detail) => ({
              ...detail,
              // Información del producto
              product_name: detail.products?.name || "Producto desconocido",
              product_type: detail.products?.is_jug
                ? "Botellón"
                : detail.products?.is_refill
                ? "Recambio"
                : "Producto",

              // Información de botellones (solo códigos de barras)
              delivered_jug_barcode: detail.delivered_jug?.barcode || null,
              returned_jug_barcode: detail.returned_jug?.barcode || null,

              // Dirección de entrega
              delivery_address:
                detail.addresses?.address || "Dirección no especificada",
              delivery_city: detail.addresses?.city || "",

              // Fechas formateadas
              scheduled_delivery_formatted: detail.scheduled_delivery_date
                ? new Date(detail.scheduled_delivery_date).toLocaleDateString(
                    "es-CO"
                  )
                : null,
              actual_delivery_formatted: detail.actual_delivery_date
                ? new Date(detail.actual_delivery_date).toLocaleDateString(
                    "es-CO"
                  )
                : null,

              // Precio formateado
              unit_price_formatted: new Intl.NumberFormat("es-CO", {
                style: "currency",
                currency: "COP",
                minimumFractionDigits: 0,
              }).format(detail.unit_price || 0),

              // Total del detalle
              detail_total: (detail.quantity || 1) * (detail.unit_price || 0),
              detail_total_formatted: new Intl.NumberFormat("es-CO", {
                style: "currency",
                currency: "COP",
                minimumFractionDigits: 0,
              }).format((detail.quantity || 1) * (detail.unit_price || 0)),
            })) || [],

          // Resumen de productos en la orden
          products_summary:
            order.order_details?.reduce((acc, detail) => {
              const productType = detail.products?.is_jug
                ? "botellones"
                : detail.products?.is_refill
                ? "recambios"
                : "otros";
              acc[productType] =
                (acc[productType] || 0) + (detail.quantity || 1);
              return acc;
            }, {}) || {},

          // Total de items
          total_items:
            order.order_details?.reduce(
              (sum, detail) => sum + (detail.quantity || 1),
              0
            ) || 0,

          // Códigos de barras únicos involucrados
          barcodes_involved: [
            ...new Set([
              ...(order.order_details
                ?.map((d) => d.delivered_jug?.barcode)
                .filter(Boolean) || []),
              ...(order.order_details
                ?.map((d) => d.returned_jug?.barcode)
                .filter(Boolean) || []),
            ]),
          ],

          // Verificar si tiene costos de envío
          has_shipping: (order.shipping_cost || 0) > 0,
          is_incomplete:
            order.order_details?.length === 0 &&
            order.order_status === "pending",
        })) || [];

      setOrders(processedOrders);
    } catch (err) {
      console.error("Error fetching user orders:", err);
      setError(err.message);
      toast.error("Error al cargar tus pedidos");
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [user?.id]);

  // Función para refrescar datos
  const refreshOrders = useCallback(async () => {
    setIsRefreshing(true);
    await fetchUserOrders();
  }, [fetchUserOrders]);

  // Función para manejar pago
  const handlePayment = useCallback(
    async (orderId) => {
      try {
        // Aquí implementarías la integración con tu pasarela de pago
        // Por ahora simular redirección
        toast.success("Redirigiendo a pasarela de pago...", {
          duration: 3000,
          position: "top-center",
        });

        // Simular actualización después del pago
        setTimeout(async () => {
          await refreshOrders();
        }, 2000);

        return true;
      } catch (error) {
        console.error("Error processing payment:", error);
        toast.error(`Error al procesar pago: ${error.message}`);
        return false;
      }
    },
    [refreshOrders]
  );

  // Función para obtener ID corto legible
  const getShortOrderId = useCallback((order) => {
    if (!order) return "N/A";
    // Usar el short_id de la orden si existe, sino usar los últimos caracteres del UUID
    return order.short_id
      ? `${order.short_id.toUpperCase()}`
      : `${order.id.toString().slice(-8).toUpperCase()}`;
  }, []);

  // Función para obtener el color del badge según el estado
  const getOrderStatusColor = useCallback((status) => {
    const colorMap = {
      pending: "gray",
      processing: "blue",
      in_transit: "blue",
      delivered: "green",
      canceled: "red",
      rejected: "red",
    };
    return colorMap[status] || "gray";
  }, []);

  const getPaymentStatusColor = useCallback((status) => {
    const colorMap = {
      pending: "orange",
      processing: "blue",
      paid: "green",
      partially_paid: "yellow",
      failed: "red",
      refunded: "gray",
      cancelled: "gray",
    };
    return colorMap[status] || "gray";
  }, []);

  // Obtener etiquetas legibles
  const getOrderStatusLabel = useCallback((status) => {
    const labels = {
      pending: "Pedido reciente",
      processing: "Procesando",
      in_transit: "En camino",
      delivered: "Entregado",
      canceled: "Cancelado",
      rejected: "Rechazado",
    };
    return labels[status] || status;
  }, []);

  const getPaymentStatusLabel = useCallback((status) => {
    const labels = {
      pending: "Pago pendiente",
      processing: "Procesando",
      paid: "Pagado",
      partially_paid: "Pago parcial",
      failed: "Falló",
      refunded: "Reembolsado",
      cancelled: "Cancelado",
    };
    return labels[status] || status;
  }, []);

  // Estadísticas computadas
  const stats = useMemo(() => {
    const total = orders.length;
    const pending = orders.filter((o) => o.payment_status === "pending").length;
    const paid = orders.filter((o) => o.payment_status === "paid").length;
    const totalAmount = orders.reduce((sum, o) => sum + (o.total || 0), 0);
    const pendingAmount = orders.reduce(
      (sum, o) =>
        o.payment_status === "pending" ? sum + (o.balance_due || 0) : sum,
      0
    );

    return {
      total,
      pending,
      paid,
      totalAmount,
      pendingAmount,
      totalAmountFormatted: new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
      }).format(totalAmount),
      pendingAmountFormatted: new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        minimumFractionDigits: 0,
      }).format(pendingAmount),
    };
  }, [orders]);

  // Cargar datos al montar y cuando cambie el usuario
  useEffect(() => {
    fetchUserOrders();
  }, [fetchUserOrders]);

  return {
    // Datos principales
    orders,
    stats,

    // Estados de carga
    isLoading,
    isRefreshing,
    error,

    // Funciones
    refreshOrders,
    handlePayment,
    getShortOrderId,
    getOrderStatusColor,
    getPaymentStatusColor,
    getOrderStatusLabel,
    getPaymentStatusLabel,

    // Estado computado
    hasOrders: orders.length > 0,
    hasPendingPayments: orders.some((o) => o.needs_payment),
  };
};
